import { useCallback, useEffect, useRef } from 'react'

/**
 * CC-49462 — frozen ("pinned") first column for wide report grids.
 *
 * The two report table renderers are independent implementations —
 * `DataGrid` (flat reports, via ReportDataGrid) and `ReportGroupedGrid` (its own
 * hand-rolled table) — so the *decisions* live here and each grid only applies
 * them. Keeping the resolution rules in one place is what stops the two from
 * drifting the way LookupFilter/ObjectLookup did.
 *
 * Design decisions (recorded in spec 081, resolved rather than escalated):
 *
 *  - **Which column pins?** Exactly one, and it is always the column at the left
 *    edge. With nothing stored, that is whatever column is currently leftmost in
 *    the user's OWN order — *not* the report's default identity column. Pinning
 *    the default identity column would silently override a persisted drag-reorder
 *    (CC-49459), pushing a column the user deliberately dragged to the front back
 *    behind one they had moved away from. A user-chosen pin *set* is a bigger
 *    feature and is deliberately not built here.
 *  - **How is the pin keyed?** By `columnId`, never by position (CC-50236 had to
 *    migrate the value filter off label matching for the same reason). An
 *    explicitly frozen column is hoisted to the left edge and stays frozen
 *    through column reorder, hide/show, and a firm renaming it via the CC-50265
 *    alias editor. Only an EXPLICIT pin hoists; the unset default simply freezes
 *    whichever column is already leftmost, so it can never fight the user's order.
 *  - **Where is it stored?** In `preferencesStore`'s existing generic per-page
 *    `pageFlags` map, as one boolean per column id under the flag name
 *    `pin:<columnId>`. Reusing the generic map (rather than adding a store
 *    method) is deliberate: a new shared-store method has to be added to every
 *    existing test mock — **45** files mock `@store/preferencesStore`, counted
 *    2026-08-28 (this line said 37, and CLAUDE.md said 43 on 8/26; the number
 *    only ever grows, so re-count it rather than quoting any of them). And
 *    `resetPagePreferences` already clears `pageFlags`, so the grid's reset
 *    unpins for free — that control is "Reset grid layout" on the report grids
 *    as of section E4 (it stays "Reset layout" on the nine non-report pages,
 *    which have no second reset to disambiguate from; DataGrid.jsx:1245-1331).
 *  - **Default?** ON for report grids, targeting the identity column. A pinned
 *    column with nothing to scroll is a visual no-op (the divider only appears
 *    once the grid is actually scrolled right), so defaulting on costs narrow
 *    reports nothing and gives the wide ones the behaviour without a setting.
 *
 * A pinned column is hoisted to the left edge and excluded from drag-reorder —
 * it cannot be dragged, and dragging another column past it does nothing. It
 * stays resizable (CC-49380) and keeps its alignment (CC-49735).
 */

/** Class the grids put on the pinned <th>/<td>. Styled in `src/index.css`. */
export const PIN_CELL_CLASS = 'cb-pin-cell'

/** Set on the scroll container while it is scrolled right, to reveal the divider. */
export const PIN_SCROLL_ATTR = 'data-cb-pin-scrolled'

/** Flag name a column's pin is stored under, inside the page's `pageFlags`. */
export const pinFlagName = (columnId) => `pin:${columnId}`

/**
 * Column ids that are never eligible to be frozen — grid infrastructure rather
 * than report data. Freezing the selection checkbox would pin an empty gutter.
 */
const NON_PINNABLE_IDS = new Set(['_select'])

/**
 * The ids that can actually be frozen right now: real data columns the user can
 * SEE, in their current order.
 *
 * Visibility is the CC-49462 EX7 lesson. A frozen column that the user then
 * hides renders no cell at all, so resolving the pin against the *full* order
 * hands back a column that is not on screen: the freeze silently stops working
 * while the toggle still reports `aria-pressed="true"` and reads "Unfreeze
 * first column". Every pin decision therefore runs over the visible set, which
 * is what makes it impossible for the control to claim a state that isn't true.
 *
 * @param {Array<string|number>} columnIds ids in the user's CURRENT order.
 * @param {(id: string) => boolean} [isVisible] returns false for a hidden
 *   column. Omitted (older callers, unit tests) ⇒ every column is visible.
 * @returns {string[]} eligible ids, order preserved.
 */
export function pinnableColumnIds(columnIds, isVisible) {
  const ids = (columnIds || []).filter((id) => id != null).map(String)
  const visible = typeof isVisible === 'function' ? ids.filter((id) => isVisible(id) !== false) : ids
  return visible.filter((id) => !NON_PINNABLE_IDS.has(id))
}

/**
 * Which column is currently frozen.
 *
 * @param {string[]} columnIds column ids in the order the user CURRENTLY has
 *   (their persisted reorder, not the report's default) — index 0 is therefore
 *   the column at the left edge, which is what an unset report defaults to.
 *   Passing the default order here instead would make the pin override a
 *   persisted drag-reorder.
 * @param {(flag: string) => boolean|null|undefined} readFlag reads one page flag.
 * @param {(id: string) => boolean} [isVisible] returns false for a hidden column.
 * @returns {string|null} the frozen column's id, or null when nothing is frozen.
 */
export function resolvePinnedColumnId(columnIds, readFlag, isVisible) {
  const ids = (columnIds || []).filter((id) => id != null).map(String)
  if (ids.length === 0) return null
  const candidates = pinnableColumnIds(ids, isVisible)
  const read = (id) => (readFlag ? readFlag(pinFlagName(id)) : null)

  // An explicit pin only counts while its column is on screen. Hiding the
  // column does NOT delete the flag — it stays stored, so showing the column
  // again restores the freeze — it just stops being the answer meanwhile.
  for (const id of candidates) if (read(id) === true) return id

  // An explicit `false` is the user switching freezing OFF for this report; it
  // is recorded per column only so it can't be confused with "never chosen".
  // It is therefore honoured even when that column is hidden — otherwise
  // hiding a column would silently switch a freeze the user turned off back on.
  for (const id of ids) if (read(id) === false) return null

  return candidates[0] ?? null
}

/**
 * The column the "Freeze first column" button should pin: the leftmost one the
 * user can see. Sharing this with `resolvePinnedColumnId` above is what stops
 * the button from writing a pin the resolver will then refuse — which is how
 * EX7's "toggling off and on again does not recover it" happened (the toggle
 * targeted the first id in the order, hidden or not).
 */
export function pinToggleTargetId(columnIds, isVisible) {
  return pinnableColumnIds(columnIds, isVisible)[0] ?? null
}

/**
 * Every id that must be written `false` when the user unfreezes.
 *
 * "Unfreeze" means OFF for this report, so a `true` still stored against some
 * other column — necessarily a hidden one, since a visible one would have been
 * the resolved pin — has to be cleared too. Leaving it would re-freeze the grid
 * the moment that column was shown again, i.e. the toggle's state changing
 * without the user touching the toggle.
 */
export function pinFlagsToClearOnUnfreeze(columnIds, readFlag, pinnedColumnId) {
  const ids = (columnIds || []).filter((id) => id != null).map(String)
  const out = pinnedColumnId ? [String(pinnedColumnId)] : []
  for (const id of ids) {
    if (out.includes(id)) continue
    if (readFlag && readFlag(pinFlagName(id)) === true) out.push(id)
  }
  return out
}

/**
 * The display order with the frozen column moved to the left edge. Returns the
 * input untouched when nothing is frozen or it is already first, so callers can
 * keep memoizing on identity.
 */
export function orderWithPinFirst(order, pinnedColumnId) {
  if (!pinnedColumnId || !Array.isArray(order)) return order
  const idx = order.indexOf(pinnedColumnId)
  if (idx <= 0) return order
  const next = order.slice()
  next.splice(idx, 1)
  next.unshift(pinnedColumnId)
  return next
}

/**
 * Ref callback for a grid's horizontal scroll container. Toggles
 * `data-cb-pin-scrolled` on the element itself as the user scrolls, so the
 * frozen column's divider/shadow only shows once there is something hidden
 * underneath it.
 *
 * Written straight to the DOM node rather than held in React state on purpose:
 * a scroll position is a ticking value, and a ticking value reaching a `columns`
 * memo has already caused two unrelated-looking grid bugs on this repo. Nothing
 * here re-renders.
 */
export function usePinnedScrollShadow(enabled) {
  const nodeRef = useRef(null)
  const enabledRef = useRef(enabled)

  const sync = useCallback(() => {
    const el = nodeRef.current
    if (!el) return
    const on = enabledRef.current && el.scrollLeft > 0
    el.setAttribute(PIN_SCROLL_ATTR, on ? 'true' : 'false')
  }, [])

  const setNode = useCallback(
    (el) => {
      if (nodeRef.current) nodeRef.current.removeEventListener('scroll', sync)
      nodeRef.current = el
      if (el) {
        el.addEventListener('scroll', sync, { passive: true })
        sync()
      }
    },
    [sync]
  )

  // Re-sync whenever the grid re-renders (freeze toggled, columns changed,
  // report re-run) so the divider matches the current state without a scroll.
  useEffect(() => {
    enabledRef.current = enabled
    sync()
  })

  // Detach on unmount — `setNode(null)` is not guaranteed for every unmount path.
  useEffect(() => () => {
    if (nodeRef.current) nodeRef.current.removeEventListener('scroll', sync)
  }, [sync])

  return setNode
}
